import { computed, ComputedRef, Ref } from 'vue';
import { cancelQueue } from '@libs/http';
import { useQueryV2 } from '@libs/use';
import type {
  SymbolResult,
  SearchSymbolsQuery,
  GroupedSymbols,
} from '@libs/api-reference';
import { RemovableRef, useStorage } from '@vueuse/core';

import * as API from '../requests';

import {
  SYMBOL_QUERY_KEYS,
  SYMBOLS_SUB_SECTOR_CODES_CATEGORY,
  SYMBOLS_TYPE_CODES_CATEGORY,
} from '../constants';
import { isOptionSymbol } from '../utils/isOption';

type UseGetSearchSymbolsReturnType = {
  symbols: ComputedRef<SymbolResult[]>;
  groupedSymbols: ComputedRef<GroupedSymbols>;
  searchSymbols: ReturnType<typeof useQueryV2>['execute'];
  isLoadingSymbols: Ref<boolean>;
  isErrorSearchSymbols: Ref<boolean>;
  isRevalidatingSymbols: Ref<boolean>;
  searchSymbolsStatus: ReturnType<typeof useQueryV2>['status'];
  recentSearchSymbols: RemovableRef<SymbolResult[]>;
  addToRecentSearchSymbols: (symbol: SymbolResult) => void;
  removeSymbolFromRecentSearch: (symbol: SymbolResult) => void;
};

const recentSearchSymbols = useStorage<SymbolResult[]>(
  'recent-search',
  [],
  localStorage,
  { mergeDefaults: false }
);

const addToRecentSearchSymbols = (symbol: SymbolResult) => {
  const index = recentSearchSymbols.value.findIndex(
    (storedSymbol) => storedSymbol.isin === symbol.isin
  );

  if (index === -1) {
    if (recentSearchSymbols.value.length > 4) {
      recentSearchSymbols.value.pop();
    }

    recentSearchSymbols.value.unshift(symbol);
  }
};

const removeSymbolFromRecentSearch = (symbol: SymbolResult) => {
  const index = recentSearchSymbols.value.findIndex(
    (storedSymbol) => storedSymbol.isin === symbol.isin
  );

  if (index > -1) {
    recentSearchSymbols.value.splice(index, 1);
  }
};

export default function useSearchSymbolQuery(
  term: Ref<string>,
  filterOption = false
): UseGetSearchSymbolsReturnType {
  const {
    result,
    execute: searchSymbols,
    isPending: isLoadingSymbols,
    isRevalidating: isRevalidatingSymbols,
    isError: isErrorSearchSymbols,
    status: searchSymbolsStatus,
  } = useQueryV2<SearchSymbolsQuery>(() => API.searchSymbols(term.value), {
    key: SYMBOL_QUERY_KEYS.searchSymbols(term),
    immediate: false,
    onSettled: cancelQueue.clearAll,
  });

  const symbols = computed(() =>
    term.value === ''
      ? []
      : (filterOption
          ? result.value?.data.items.filter((item) =>
              isOptionSymbol(item.instrumentTypeCode)
            )
          : result.value?.data.items) || []
  );

  const groupedSymbols = computed<GroupedSymbols>(() => {
    return symbols.value.reduce(
      (grouped: GroupedSymbols, symbol: SymbolResult) => {
        let symbolClass = 'others';

        if (symbol.instrumentTypeCode) {
          symbolClass = SYMBOLS_TYPE_CODES_CATEGORY[symbol.instrumentTypeCode];
        } else if (symbol.subSectorCode) {
          symbolClass = SYMBOLS_SUB_SECTOR_CODES_CATEGORY[symbol.subSectorCode];
        }

        symbolClass
          ? grouped[symbolClass].push(symbol)
          : grouped.others.push(symbol);

        return grouped;
      },
      {
        ETF: [],
        share: [],
        shareRight: [],
        commodityDepositCertificate: [],
        mortgageBond: [],
        treasuryBond: [],
        debtBond: [],
        option: [],
        others: [],
      }
    );
  });

  return {
    symbols,
    groupedSymbols,
    searchSymbols,
    recentSearchSymbols,
    addToRecentSearchSymbols,
    isRevalidatingSymbols,
    isLoadingSymbols: computed(() => Boolean(isLoadingSymbols)),
    removeSymbolFromRecentSearch,
    isErrorSearchSymbols,
    searchSymbolsStatus,
  };
}
